/**
 * ResultsPage.jsx
 *
 * Tool Finder Results page (D9). Renders the filtered product grid
 * surfaced by ProductContext, plus the recommended-grade callout, facet
 * sidebar / mobile drawer, and the sticky compare bar.
 *
 * PORTED VERBATIM from Top Hammer ToolFinder v4 (Group D Sub-Plan 3, Task
 * SP3.B.2):
 *   - Source: `tophammer-toolfinder-v4/src/pages/ResultsPage.jsx`
 *     (316 lines, full file).
 *
 * Behaviour contract (preserved from TF v4):
 *   - Multi-tab navigation driven by `visibleTabs` + `tabCounts` from
 *     ProductContext. Desktop renders a tab strip; mobile renders a
 *     dropdown.
 *   - Phase-4.7 thread refinement chip with X-to-clear shown when
 *     `refinedThreadSystem` is set.
 *   - Active-filter Clear-all link rendered when `activeFilterCount > 0`.
 *   - Mobile filters open a full-screen drawer with a sticky "Show N
 *     Results" footer button.
 *   - Desktop grid/list view toggle (mobile is always grid).
 *   - Empty state with Clear-all when `filteredProducts.length === 0`.
 *   - StickyCompareBar appears when the compare context is non-empty.
 *
 * MRT adaptations (only):
 *   1. Material Symbols Icon → lucide-react named icons:
 *        refresh        → RefreshCw
 *        filter_alt     → Filter
 *        close          → X
 *        expand_less    → ChevronUp
 *        expand_more    → ChevronDown
 *        grid_view      → LayoutGrid
 *        view_list      → List
 *        search_off     → SearchX
 *   2. Sandvik no-rounded-corners rule — every `rounded-lg`/`rounded`
 *      stripped (exception: `rounded-full` on the X button hover halo
 *      around the thread chip remains, per brand guidance for tap
 *      targets on small chips).
 *   3. `useMediaQuery` in MRT returns `{ isDesktop, isMobile, isTablet,
 *      breakpoint }` (object), not a boolean — destructure isDesktop.
 *   4. CompareContext shape differs: no `compareItems`/`compareProducts`/
 *      `toggleCompare`. MRT exposes `compareList`, `addToCompare`,
 *      `removeFromCompare`, `clearCompare`, `isInCompare`. The PLP card
 *      `onCompareToggle` is wired to add-or-remove based on isInCompare.
 *   5. TF v4 imports `setContactProduct` from `useOutletContext()` to
 *      open a contact modal. MRT has no such layout-level modal yet;
 *      we route to `/contact?topic=availability&item={itemCode}` so the
 *      destination is harmonised with RegionAvailabilityBadge.
 *   6. Product detail navigation — MRT route is `/products/detail/:itemCode`
 *      (see App.jsx). TF v4 used `/product/${itemCode}`.
 *   7. Restart link calls `resetWizard()` + `clearAllFilters()` and
 *      navigates to `/toolfinder` (wizard root) instead of `/` (TF v4
 *      site root) because the wizard owns its own entry route in MRT.
 *   8. New `<GradeRecommendationCallout />` block at the top of the
 *      product-grid column. MRT addition — no TF v4 equivalent (TF v4's
 *      bit-grade logic lives in Apex / BitSelectionGuide__mdt).
 *   9. `<RegionAvailabilityBadge itemCode={...} />` passed into each
 *      `<PLPProductCard>` via the new `availabilityBadge` slot prop
 *      (SP3.B.1). Component returns null for in-region products so no
 *      whitespace leaks into otherwise-clean cards.
 *  10. StickyCompareBar reads the compare context directly in MRT
 *      (Group C contract), so it is rendered without prop drilling.
 *
 * @module pages/ResultsPage
 * @since 2026-05-18 (Group D Sub-Plan 3, Task SP3.B.2)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  Filter,
  X,
  ChevronUp,
  ChevronDown,
  LayoutGrid,
  List,
  SearchX,
} from 'lucide-react';
import useMediaQuery from '@hooks/useMediaQuery';
import { useWizard } from '@contexts/WizardContext';
import { useProducts } from '@contexts/ProductContext';
import { useCompare } from '@contexts/CompareContext';
import { useFavourites } from '@contexts/FavouritesContext';
import { FacetSection } from '@components/product/FacetSection';
import { PLPProductCard } from '@components/product/PLPProductCard';
import RegionAvailabilityBadge from '@components/wizard/RegionAvailabilityBadge';
import StickyCompareBar from '@components/compare/StickyCompareBar';
import GradeRecommendationCallout from '@components/wizard/GradeRecommendationCallout';
import MiniCart from '@components/layout/MiniCart';
import { useCart } from '@contexts/CartContext';
import useAuth from '@hooks/useAuth';

export function ResultsPage() {
  const { isDesktop } = useMediaQuery();
  const navigate = useNavigate();

  const { resetWizard } = useWizard();
  const {
    activeTab, handleTabChange, tabCounts, visibleTabs,
    filteredProducts, currentAttributes, preFilteredProducts,
    facetsWithCounts, activeFilters, toggleFilterValue,
    clearAllFilters, activeFilterCount,
    refinedThreadSystem, handleClearThreadRefinement,
  } = useProducts();
  const {
    compareList,
    addToCompare,
    removeFromCompare,
    isInCompare,
  } = useCompare();
  const { dispatch: cartDispatch } = useCart();
  const { hasPermission, isGuest } = useAuth();
  const canOrder = hasPermission('order');
  const { lists: favLists, addItem: addFavItem, removeItem: removeFavItem } = useFavourites();

  // Favourites: check against default list
  const defaultFavList = favLists.find(l => l.id === 'fav-default') || favLists[0];
  const isFavourite = (itemCode) =>
    defaultFavList?.items.some(i => i.itemCode === itemCode) ?? false;

  const handleFavoriteToggle = (product) => {
    if (!defaultFavList) return;
    if (isFavourite(product.itemCode)) {
      removeFavItem(defaultFavList.id, product.itemCode);
    } else {
      addFavItem(defaultFavList.id, {
        itemCode: product.itemCode,
        description: product.description,
        productLine: product.productLine,
        subcategory: product.subcategory,
        imageUrl: product.imageUrl || null,
      });
    }
  };

  // Local UI state
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [userViewMode, setUserViewMode] = useState('grid');
  const viewMode = isDesktop ? userViewMode : 'grid';

  const handleTabSelect = (tabId) => {
    setCategoryDropdownOpen(false);
    handleTabChange(tabId);
  };

  const handleRestart = () => {
    resetWizard();
    clearAllFilters();
    navigate('/toolfinder');
  };

  const handleProductClick = (product) => {
    navigate(`/products/detail/${encodeURIComponent(product.itemCode)}`, {
      state: { product, from: '/toolfinder/results' },
    });
  };

  const handleCompareToggle = (product) => {
    if (isInCompare(product.itemCode)) {
      removeFromCompare(product.itemCode);
    } else {
      addToCompare(product);
    }
  };

  const handleContactClick = (product) => {
    navigate(
      `/contact?topic=availability&item=${encodeURIComponent(product.itemCode)}`
    );
  };

  // G4: Add to cart from PLP — dispatches to CartContext, item appears
  // immediately in the MiniCart sidebar (desktop) or FAB badge (mobile).
  const handleAddToCart = (product) => {
    cartDispatch({
      type: 'ADD_ITEM',
      payload: {
        productId: product.itemCode,
        name: product.description,
        sku: product.itemCode,
        itemCode: product.itemCode,
        description: product.description,
        thumbnailUrl: product.imageUrl || null,
        productLine: product.productLine,
        subcategory: product.subcategory,
        quantity: 1,
      },
    });
  };

  const totalProductCount = Object.values(preFilteredProducts || {}).reduce(
    (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0
  );

  return (
    <>
      <div>
        {/* Results Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-sandvik-headline text-2xl font-normal text-carbon-black">
              These are your rock tool options
            </h1>
            <p className="font-sandvik-text text-n6">
              {filteredProducts.length} of {totalProductCount} products
            </p>
          </div>
          <button
            onClick={handleRestart}
            className="text-sm text-electric-blue hover:underline flex items-center gap-1"
            data-testid="restart-toolfinder"
          >
            <RefreshCw size={16} aria-hidden="true" />
            Restart tool finder
          </button>
        </div>

        {/* Category Tabs — Desktop (Phase 4.5a: dynamic from visibleTabs) */}
        {isDesktop && (
          <div
            className="flex border-b border-n2 mb-6 overflow-x-auto"
            data-testid="results-tabs-desktop"
          >
            {visibleTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => handleTabSelect(tab.key)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  activeTab === tab.key
                    ? 'border-electric-blue text-electric-blue'
                    : 'border-transparent text-n6 hover:text-carbon-black'
                }`}
              >
                {tab.label} ({tabCounts[tab.key] ?? 0})
              </button>
            ))}
          </div>
        )}

        {/* Category Dropdown — Mobile/Tablet (Phase 4.5a: dynamic) */}
        {!isDesktop && (
          <div className="relative mb-4" data-testid="results-tabs-mobile">
            <button
              onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
              className="w-full p-3 bg-white border border-n3 flex items-center justify-between"
            >
              <span className="font-medium">
                {visibleTabs.find(t => t.key === activeTab)?.label} ({tabCounts[activeTab] ?? 0})
              </span>
              {categoryDropdownOpen
                ? <ChevronUp size={20} aria-hidden="true" />
                : <ChevronDown size={20} aria-hidden="true" />}
            </button>
            {categoryDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-n2 shadow-lg z-20">
                {visibleTabs.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabSelect(tab.key)}
                    className={`w-full px-4 py-3 text-left hover:bg-n1 ${
                      activeTab === tab.key ? 'bg-eb-1/20 text-electric-blue' : ''
                    }`}
                  >
                    {tab.label} ({tabCounts[tab.key] ?? 0})
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Phase 4.7: Thread refinement chip */}
            {refinedThreadSystem && (
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-electric-blue/10 border border-electric-blue/30 text-electric-blue text-sm"
                data-testid="thread-refinement-chip"
              >
                Thread: {refinedThreadSystem}
                <button
                  onClick={handleClearThreadRefinement}
                  className="hover:bg-electric-blue/20 rounded-full p-0.5 transition-colors"
                  title="Show all threads"
                  aria-label="Clear thread refinement"
                  data-testid="thread-refinement-clear"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </span>
            )}
            {activeFilterCount > 0 && (
              <>
                <span className="text-sm text-n6">Active filters:</span>
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-electric-blue hover:underline"
                >
                  Clear all
                </button>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!isDesktop && (
              <button
                onClick={() => setShowMobileFilters(true)}
                className="flex items-center gap-2 px-3 py-2 border border-n3"
              >
                <Filter size={18} aria-hidden="true" />
                <span className="text-sm">Filters</span>
                {activeFilterCount > 0 && (
                  <span className="bg-vibrant-orange text-white text-xs px-1.5">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            )}

            {isDesktop && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-n6">View:</span>
                <div className="flex border border-n3 overflow-hidden">
                  <button
                    onClick={() => setUserViewMode('grid')}
                    className={`p-2 ${userViewMode === 'grid' ? 'bg-n1' : 'hover:bg-n1'}`}
                    aria-label="Grid view"
                  >
                    <LayoutGrid
                      size={18}
                      className={userViewMode === 'grid' ? 'text-electric-blue' : 'text-n5'}
                      aria-hidden="true"
                    />
                  </button>
                  <button
                    onClick={() => setUserViewMode('list')}
                    className={`p-2 ${userViewMode === 'list' ? 'bg-n1' : 'hover:bg-n1'}`}
                    aria-label="List view"
                  >
                    <List
                      size={18}
                      className={userViewMode === 'list' ? 'text-electric-blue' : 'text-n5'}
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex gap-6">
          {/* Filter Sidebar — Desktop */}
          {isDesktop && (
            <aside className="w-64 flex-shrink-0">
              <div className="bg-white border border-n2 sticky top-20">
                <div className="p-4 border-b border-n2">
                  <div className="flex items-center gap-2">
                    <Filter size={20} className="text-n6" aria-hidden="true" />
                    <span className="font-normal">Filters</span>
                    {activeFilterCount > 0 && (
                      <span className="bg-vibrant-orange text-white text-xs px-2 py-0.5">
                        {activeFilterCount}
                      </span>
                    )}
                  </div>
                </div>
                {facetsWithCounts.map(facet => (
                  <FacetSection
                    key={facet.id}
                    facet={facet}
                    activeFilters={activeFilters}
                    onToggle={toggleFilterValue}
                  />
                ))}
              </div>
            </aside>
          )}

          {/* Product Grid/List */}
          <div className="flex-1 min-w-0">
            <GradeRecommendationCallout />

            {filteredProducts.length === 0 ? (
              <div
                className="bg-white border border-n2 p-8 text-center"
                data-testid="results-empty-state"
              >
                <SearchX
                  size={48}
                  className="text-n4 mx-auto mb-4"
                  aria-hidden="true"
                />
                <p className="text-n6">
                  No products match your current filters.
                </p>
                <button
                  onClick={clearAllFilters}
                  className="mt-4 text-electric-blue hover:underline"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className={
                viewMode === 'grid'
                  ? `grid grid-cols-1 md:grid-cols-2 ${isDesktop ? '2xl:grid-cols-3' : 'xl:grid-cols-3'} gap-4`
                  : 'space-y-4'
              }>
                {filteredProducts.map(product => (
                  <PLPProductCard
                    key={product.itemCode}
                    product={product}
                    attributes={currentAttributes}
                    viewMode={viewMode}
                    onProductClick={() => handleProductClick(product)}
                    onFavoriteToggle={() => handleFavoriteToggle(product)}
                    isFavorite={isFavourite(product.itemCode)}
                    onCompareToggle={() => handleCompareToggle(product)}
                    isComparing={isInCompare(product.itemCode)}
                    onContactClick={() => handleContactClick(product)}
                    onAddToCart={canOrder ? () => handleAddToCart(product) : undefined}
                    availabilityBadge={<RegionAvailabilityBadge itemCode={product.itemCode} />}
                  />
                ))}
              </div>
            )}
          </div>

          {/* MiniCart Sidebar — Desktop only, authenticated users only (G4 + G9)
              Mobile cart access handled by FloatingCartFAB.
              Guests see no cart sidebar (they can't add items). */}
          {isDesktop && canOrder && (
            <aside className="w-72 flex-shrink-0" data-testid="minicart-column">
              <MiniCart />
            </aside>
          )}
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filter-drawer">
          <div
            className="absolute inset-0 bg-carbon-black/50"
            onClick={() => setShowMobileFilters(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[90vw] bg-white shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-n2">
              <div className="flex items-center gap-2">
                <Filter size={20} aria-hidden="true" />
                <span className="font-normal">Filters</span>
              </div>
              <button
                onClick={() => setShowMobileFilters(false)}
                aria-label="Close filters"
              >
                <X size={24} aria-hidden="true" />
              </button>
            </div>
            <div className="overflow-y-auto h-[calc(100%-140px)]">
              {facetsWithCounts.map(facet => (
                <FacetSection
                  key={facet.id}
                  facet={facet}
                  activeFilters={activeFilters}
                  onToggle={toggleFilterValue}
                />
              ))}
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-n2">
              <button
                onClick={() => setShowMobileFilters(false)}
                className="w-full py-3 bg-electric-blue text-white"
              >
                Show {filteredProducts.length} Results
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Compare Bar — reads CompareContext directly in MRT */}
      {compareList.length > 0 && <StickyCompareBar />}
    </>
  );
}

export default ResultsPage;
